import React, { useMemo } from 'react';
import type { PageProps } from '../types';
import { useAuditStore } from '../stores/auditStore';
import { NoIssuesFound } from '../components/icons/NoIssueFound';

type DeviceKey = 'mobile' | 'desktop';

function deviceToKey(d: string): DeviceKey {
  if (d === 'mobile' || d === 'desktop') return d;
  return 'desktop';
}

function deriveWcagVersion(issue: any): string | null {
  const v = issue?.wcag_version_number;
  if (v) return v.includes('.') ? `WCAG ${v}` : `WCAG ${v}.0`;
  const list: string[] = issue?.standard_list_json || [];
  if (list.includes('wcag2.2')) return 'WCAG 2.2';
  if (list.includes('wcag2.1')) return 'WCAG 2.1';
  if (list.includes('wcag2') || list.includes('wcag2.0')) return 'WCAG 2.0';
  return null;
}

export const IssueListingPage: React.FC<PageProps> = ({ onNavigate }) => {
  const { auditResult, selectedDevice, setSelectedIssue, setSelectedIssueId, setSelectedIssueCategory, wcagFilter, setWcagFilter } = useAuditStore();
  const currentDevice = deviceToKey(selectedDevice);
  const otherDevice = currentDevice === 'desktop' ? 'mobile' : 'desktop';
  const wcagFilterDisplay = useMemo(() => {
    const [version, level] = wcagFilter.split('_');
    return `WCAG ${version} ${level}`;
  }, [wcagFilter]);
  const normalizeVersion = (v?: string) => {
    if (!v) return undefined;
    const s = String(v).trim();
    if (s === '2') return '2.0';
    return s.includes('.') ? s : `${s}.0`;
  };

  const levelRank = (lvl?: string) => {
    const L = String(lvl || '').toUpperCase();
    if (L === 'A') return 1;
    if (L === 'AA') return 2;
    if (L === 'AAA') return 3;
    return 0;
  };

  const matchesFilter = (issue: any) => {
    if (wcagFilter === 'all') return true;
    const [selVerStr, selLevel] = wcagFilter.split('_');
    const selVer = parseFloat(selVerStr);
    const issueVerStr = normalizeVersion(issue?.wcag_version_number);
    const issueVer = issueVerStr ? parseFloat(issueVerStr) : NaN;
    const selRank = levelRank(selLevel);
    const issRank = levelRank(issue?.wcag_level);
    if (isNaN(issueVer)) return false;
    return issueVer <= selVer && issRank <= selRank;
  };

  const issues = useMemo(() => {
    const data = (auditResult as any)?.body?.data?.report_data || {};
    const list: any[] = Array.isArray((data as any)[currentDevice]) ? (data as any)[currentDevice] : [];
    return list.filter(matchesFilter);
  }, [auditResult, currentDevice, wcagFilter]);

  const bestPractices = useMemo(() => {
    const data = (auditResult as any)?.body?.data?.report_data || {};
    const bp = data.best_practices || {};
    const list: any[] = Array.isArray(bp[currentDevice]) ? bp[currentDevice] : [];
    return list;
  }, [auditResult, currentDevice]);

  if (!auditResult) {
    return (
      <div className="cy:text-gray-700 cy:p-4">
        Run an accessibility audit first from the Overview page.
      </div>
    );
  }

  return (
    <div className="cy:flex cy:flex-col cy:gap-3">
      {/* WCAG version/level filter */}
      <div className="cy:mb-2 cy:p-4 cy:bg-[#fff]">
        <select
          name="wcag-filter"
          id="wcag-filter"
          style={{ border: '1px solid black' }}
          className="cy:w-full cy:border cy:border-gray-300 cy:px-3 cy:py-2 cy:bg-white"
          value={wcagFilter}
          onChange={(e) => setWcagFilter(e.target.value)}
          aria-label="Filter by WCAG version and level"
        >
          <option value="2.0_A">WCAG 2.0 A</option>
          <option value="2.0_AA">WCAG 2.0 AA</option>
          <option value="2.0_AAA">WCAG 2.0 AAA</option>
          <option value="2.1_A">WCAG 2.1 A</option>
          <option value="2.1_AA">WCAG 2.1 AA</option>
          <option value="2.1_AAA">WCAG 2.1 AAA</option>
          <option value="2.2_A">WCAG 2.2 A</option>
          <option value="2.2_AA">WCAG 2.2 AA</option>
          <option value="2.2_AAA">WCAG 2.2 AAA</option>
        </select>
      </div>

      <div className="cy:space-y-1 cy:px-4">
        {issues.length === 0 && bestPractices.length === 0 ? (
          <div className="cy:flex cy:flex-col cy:items-center cy:justify-center cy:py-12 cy:px-4">
            <NoIssuesFound width={164} height={152} />
            <h3 className="cy:mt-4 cy:text-lg cy:font-semibold cy:text-gray-900">
              No issues found on {currentDevice}
            </h3>
            <p className="cy:mt-2 cy:text-sm cy:text-gray-600 cy:text-center">
              Good going! There are no accessibility issues found in automated scan for {wcagFilterDisplay} on {currentDevice} version of the page.<br/>
              Be sure to review the {otherDevice} version for any remaining issues.
            </p>
          </div>
        ) : (
          issues.map((issue: any, idx: number) => {
            const affected = Number(issue?.issue_content_json?.details?.items?.length || 0);
            const wcag = deriveWcagVersion(issue);
            return (
              <div
                key={idx}
                className="cy:bg-white cy:rounded-lg cy:border cy:border-gray-200 cy:p-3 cy:flex cy:items-start cy:justify-between cy:cursor-pointer cy:hover:bg-gray-50"
                onClick={() => {
                  setSelectedIssueCategory('wcag');
                  setSelectedIssue(issue);
                  setSelectedIssueId(issue?.issue_id || null);
                  onNavigate('accessibilityIssueDetails');
                }}
              >
                <div className="cy:flex cy:items-start cy:gap-3 cy:pr-2">
                  <div className="cy:h-2.5 cy:w-2.5">
                    <span className="cy:mt-1 cy:inline-block cy:w-2.5 cy:h-2.5 cy:bg-red-500 cy:rounded-full"></span>
                  </div>
                  <div>
                    <div className="cy:font-semibold cy:text-gray-900">{issue?.issue_title || issue?.issue_id}</div>
                    <div className="cy:text-xs cy:text-gray-500 cy:mt-0.5">{wcag || 'WCAG'}</div>
                    <div className="cy:text-xs cy:text-gray-500 cy:mt-0.5">Elements affected: {affected}</div>
                  </div>
                </div>
                <svg className="cy:w-4 cy:h-4 cy:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            );
          })
        )}
      </div>

      {/* Best practices section */}
      {bestPractices.length > 0 && (
        <div className="cy:px-4 cy:pt-4 cy:pb-2">
          <div className="cy:flex cy:items-center cy:gap-2 cy:mb-2">
            <h3 className="cy:text-base cy:font-semibold cy:text-gray-900 cy:m-0">Best practices</h3>
            <span className="cy:inline-flex cy:items-center cy:px-2 cy:py-0.5 cy:rounded cy:text-xs cy:font-medium cy:bg-purple-100 cy:text-purple-700" aria-hidden="true">New</span>
          </div>
          <div className="cy:space-y-1">
            {bestPractices.map((issue: any, idx: number) => {
              const affected = Number(issue?.issue_content_json?.details?.items?.length || 0);
              const wcagLabel = deriveWcagVersion(issue) || 'Best practice';
              return (
                <div
                  key={idx}
                  className="cy:bg-white cy:rounded-lg cy:border cy:border-gray-200 cy:p-3 cy:flex cy:items-start cy:justify-between cy:cursor-pointer cy:hover:bg-gray-50"
                  onClick={() => {
                    setSelectedIssueCategory('best-practice');
                    setSelectedIssue(issue);
                    setSelectedIssueId(issue?.issue_id || null);
                    onNavigate('accessibilityIssueDetails');
                  }}
                >
                  <div className="cy:flex cy:items-start cy:gap-3 cy:pr-2">
                    <div className="cy:h-2.5 cy:w-2.5">
                      <span className="cy:mt-1 cy:inline-block cy:w-2.5 cy:h-2.5 cy:bg-amber-500 cy:rounded-full"></span>
                    </div>
                    <div>
                      <div className="cy:font-semibold cy:text-gray-900">{issue?.issue_title || issue?.issue_id}</div>
                      <div className="cy:text-xs cy:text-gray-500 cy:mt-0.5">{wcagLabel}</div>
                      <div className="cy:text-xs cy:text-gray-500 cy:mt-0.5">Elements affected: {affected}</div>
                    </div>
                  </div>
                  <svg className="cy:w-4 cy:h-4 cy:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

